import { Link } from "react-router-dom";
import {
  Printer, Download, ArrowLeft, ShieldOff, Leaf, Zap, GaugeCircle,
  CheckCircle2, Phone, AlertTriangle,
} from "lucide-react";
import { IMAGES, COMPANY } from "../lib/data";

const BENEFITS = [
  { icon: ShieldOff, t: "Non-hazardous, no restricted ingredients", d: "A water-based nonionic-surfactant formulation with no caustic, hydrocarbon solvents, or d-limonene — non-hazardous per CERCLA, OSHA, and CA Prop 65, and TSCA compliant. It is not a fluorinated (AFFF-type) foam." },
  { icon: Leaf, t: "Supports natural bioremediation", d: "Water-based and low-residue; emulsified hydrocarbons remain available to naturally occurring bacteria, supporting downstream bioremediation." },
  { icon: Zap, t: "Rapid emulsification", d: "Micro-emulsifies fuels and oils on contact when agitated, so crews knock down spills and clean surfaces fast." },
  { icon: GaugeCircle, t: "Reduces vapor & flammability", d: "Encapsulation dramatically reduces vapor pressure and, when properly agitated, makes a non-ignited spill non-flammable — lowering explosion risk during response and cleanup." },
];

export default function PinkwaterMarketingFlyer() {
  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      <div className="sticky top-0 z-40 bg-ink text-white border-b border-white/10 print:hidden">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/products/pinkwater" className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to Pinkwater
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[11px] uppercase tracking-widest text-white/50">PinkWater · Fire & Hazmat Flyer</span>
            <a href="/pinkwater-fire-marketing-flyer.pdf" download data-testid="download-pw-marketing" className="inline-flex items-center gap-2 border border-white/25 text-white px-4 py-2 text-sm font-semibold hover:border-flame hover:text-flame transition-colors">
              <Download className="h-4 w-4" /> Download PDF
            </a>
            <button onClick={() => window.print()} data-testid="print-pw-marketing" className="inline-flex items-center gap-2 bg-flame text-white px-4 py-2 text-sm font-semibold hover:bg-flame/90 transition-colors">
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        </div>
      </div>

      <div className="py-8 print:py-0 flex justify-center">
        <div data-testid="pw-marketing-sheet" className="bg-white shadow-2xl print:shadow-none w-[210mm] h-[297mm] flex flex-col" style={{ fontSize: "10.5px" }}>
          {/* hero */}
          <div className="relative h-[62mm] overflow-hidden flex-shrink-0">
            <img src={IMAGES.fireGear} alt="Fire and hazmat response" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/40" />
            <div className="relative h-full p-[12mm] flex flex-col justify-between">
              <div className="flex items-center gap-2">
                <img src={IMAGES.markWhite} alt="OneHarvest" className="h-7 w-7 object-contain" />
                <span className="font-display font-extrabold tracking-tight text-white text-base">ONEHARVESTCO.</span>
              </div>
              <div>
                <span className="inline-block border border-flame/50 bg-flame/20 text-white px-2.5 py-1 text-[9px] uppercase tracking-[0.2em]">Non-Fluorinated Hydrocarbon Mitigation</span>
                <h1 className="mt-3 font-display font-black text-white text-[40px] leading-[0.95] tracking-tight">BioSolve PinkWater</h1>
                <p className="mt-2 text-white/85 text-[13px] max-w-[120mm]">Spill response · vapor suppression · equipment decon — for fire departments and hazmat teams carrying PFAS liability.</p>
              </div>
            </div>
          </div>

          {/* body */}
          <div className="p-[12mm] flex-1 flex flex-col">
            {/* problem */}
            <div className="border-l-4 border-flame pl-4">
              <div className="flex items-center gap-2 text-flame">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-[9px] uppercase tracking-[0.18em] font-semibold">The problem you're already living with</span>
              </div>
              <p className="mt-2 text-charcoal text-[12px] leading-snug">
                Legacy AFFF leaves departments with <span className="font-semibold text-ink">PFAS liability, costly disposal, and firefighter exposure risk</span> — not just during fire attack, but every time crews handle foam, run spill cleanup, and decon apparatus and PPE. Those adjacent tasks carry PFAS risk regardless of your foam-transition timeline.
              </p>
            </div>

            {/* positioning */}
            <div className="mt-5 bg-ink text-white p-5">
              <div className="font-display font-bold text-[16px]">A low-risk addition — not a trade-off.</div>
              <p className="mt-2 text-white/75 text-[11.5px] leading-snug">
                PinkWater is a non-fluorinated hydrocarbon mitigation agent for <span className="text-white font-semibold">non-ignited spill response, vapor suppression, and decontamination</span>. It is <span className="text-white font-semibold">not an AFFF replacement and does not extinguish active Class B fires</span> — you keep your existing fire-attack capability and simply reduce PFAS exposure in the areas around it.
              </p>
            </div>

            {/* benefits */}
            <div className="mt-6">
              <div className="text-[9px] uppercase tracking-[0.18em] text-flame font-semibold mb-3">Why departments add it</div>
              <div className="grid grid-cols-2 gap-4">
                {BENEFITS.map((b) => (
                  <div key={b.t} className="flex items-start gap-3">
                    <div className="h-9 w-9 flex-shrink-0 flex items-center justify-center bg-flame/10 border border-flame/25 text-flame">
                      <b.icon className="h-5 w-5" strokeWidth={2} />
                    </div>
                    <div>
                      <div className="font-display font-bold text-ink text-[12px]">{b.t}</div>
                      <p className="text-charcoal text-[10.5px] leading-snug mt-0.5">{b.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* trust */}
            <div className="mt-6 border border-border p-5 bg-muted/40">
              <div className="text-[9px] uppercase tracking-[0.18em] text-flame font-semibold">Trusted by fire departments since the 1980s</div>
              <p className="mt-2 text-charcoal text-[11.5px] leading-snug">
                Fire departments have relied on BioSolve PinkWater since the early 1980s. Documented users include <span className="font-semibold text-ink">FDNY Hazmat Units</span> and <span className="font-semibold text-ink">New York City Transit</span>, and it is used across <span className="font-semibold text-ink">hundreds of fire departments and hazmat teams nationwide.</span>
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {["FDNY Hazmat Units", "New York City Transit", "Hundreds of departments nationwide"].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 border border-border bg-white px-2.5 py-1 text-[9.5px] text-ink">
                    <CheckCircle2 className="h-3 w-3 text-flame" /> {t}
                  </span>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="mt-auto pt-6 flex items-end justify-between gap-4">
              <div>
                <div className="font-display font-black text-ink text-[22px] leading-tight">See it work. Schedule a demo.</div>
                <p className="text-charcoal text-[11px] mt-1">We'll walk your team through spill, vapor, and decon applications — on your terms.</p>
                <div className="mt-2 flex items-center gap-2 text-ink text-[12px] font-semibold">
                  <Phone className="h-4 w-4 text-flame" /> {COMPANY.phone} · {COMPANY.email}
                </div>
              </div>
              <Link to="/request-quote" className="inline-flex items-center bg-flame text-white px-6 py-3.5 font-semibold text-[13px] hover:bg-flame/90 transition-colors print:hidden flex-shrink-0">
                Request a Demo
              </Link>
            </div>

            <p className="mt-4 pt-2 border-t border-border text-[8px] text-muted-foreground leading-tight">
              Composition/status per manufacturer documentation (BioSolve product page &amp; SDS): water-based nonionic surfactants; no caustic, hydrocarbon solvents, or d-limonene; non-hazardous (CERCLA/OSHA/Prop 65); TSCA compliant. An explicit "PFAS-free" claim, and the specific department references and "since the 1980s" date, should be confirmed in writing with the manufacturer before external publication. PinkWater is a hydrocarbon mitigation agent for non-ignited spill response, vapor suppression, and decontamination; it is not a fire-suppression foam and is not intended to extinguish active Class B fires. Outcomes vary by product, dilution, and conditions. © {new Date().getFullYear()} {COMPANY.legal}.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
